/**
 * 数据字典工具类
 */
import { useDictStoreWithOut } from '@/store/modules/dict'
import { ElementPlusInfoType } from '@/types/elementPlus'

const dictStore = useDictStoreWithOut()

/**
 * 获取 dictType 对应的数据字典数组
 *
 * @param dictType 数据类型
 * @returns {*|Array} 数据字典数组
 */
export interface DictDataType {
  dictType: string
  label: string
  value: string | number | boolean
  colorType: ElementPlusInfoType | ''
  cssClass: string
}

export interface NumberDictDataType extends DictDataType {
  value: number
}

export interface StringDictDataType extends DictDataType {
  value: string
}

export const getDictOptions = (dictType: string) => {
  // 渲染期按需触发：首次读到某类型时自动发一次请求，已加载/在途则空转，
  // 所以「进入哪个页面就拉哪个页面的字典」由消费方自己决定，无需集中预加载。
  dictStore.requestDictType(dictType)
  return dictStore.getDictByType(dictType) || []
}

export const getIntDictOptions = (dictType: string): NumberDictDataType[] => {
  // 获得通用的 DictDataType 列表
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  // 转换成 number 类型的 NumberDictDataType 类型
  // why 需要特殊转换：避免 IDEA 在 v-for="dict in getIntDictOptions(...)" 时，el-option 的 key 会告警
  const dictOption: NumberDictDataType[] = []
  dictOptions.forEach((dict: DictDataType) => {
    dictOption.push({
      ...dict,
      value: parseInt(dict.value + '')
    })
  })
  return dictOption
}

export const getStrDictOptions = (dictType: string): StringDictDataType[] => {
  const dictOption: StringDictDataType[] = []
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  dictOptions.forEach((dict: DictDataType) => {
    dictOption.push({
      ...dict,
      value: dict.value + ''
    })
  })
  return dictOption
}

export const getBoolDictOptions = (dictType: string) => {
  const dictOption: DictDataType[] = []
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  dictOptions.forEach((dict: DictDataType) => {
    dictOption.push({
      ...dict,
      value: dict.value + '' === 'true'
    })
  })
  return dictOption
}

/**
 * 取字典选项并剔除「只用于展示、不进下拉」的兼容值。
 *
 * 后端存在几组新老两套码却共用同一文案的情况（如跟进方式 PHONE / CALL 都叫「电话」），
 * 它们要能显示标签、但不该出现在筛选下拉里，否则会看到两个一样的选项。
 *
 * @param dictType 数据类型
 * @param hiddenValues 需要从下拉里剔除的字典值
 * @param T 值的联合类型；字典条目的 code 运行时是字符串，调用方可传具体联合类型拿回类型约束
 */
export const getSelectableDictOptions = <T extends string = string>(
  dictType: string,
  hiddenValues: string[] = []
): { label: string; value: T }[] =>
  getStrDictOptions(dictType)
    .filter((option) => !hiddenValues.includes(option.value))
    .map((option) => ({ label: option.label, value: option.value as T }))

/**
 * 获取指定字典类型的指定值对应的字典对象
 * @param dictType 字典类型
 * @param value 字典值
 * @return DictDataType 字典对象
 */
export const getDictObj = (dictType: string, value: any): DictDataType | undefined => {
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  for (const dict of dictOptions) {
    if (dict.value === value + '') {
      return dict
    }
  }
}

/**
 * 获得字典数据的文本展示
 *
 * @param dictType 字典类型
 * @param value 字典数据的值
 * @return 字典名称
 */
export const getDictLabel = (dictType: string, value: any): string => {
  const dictOptions: DictDataType[] = getDictOptions(dictType)
  const dictLabel = ref('')
  dictOptions.forEach((dict: DictDataType) => {
    if (dict.value === value + '') {
      dictLabel.value = dict.label
    }
  })
  return dictLabel.value
}

export enum DICT_TYPE {
  USER_TYPE = 'user_type',
  COMMON_STATUS = 'common_status',
  TERMINAL = 'terminal', // 终端
  DATE_INTERVAL = 'date_interval', // 数据间隔

  // ========== 基础主数据（basedata 的 SYSTEM 类字典）==========
  REGION = 'REGION', // 行政区划（含华东/华北等大区，看板分区分组复用）

  // ========== SYSTEM 模块 ==========
  SYSTEM_USER_SEX = 'system_user_sex',
  SYSTEM_MENU_TYPE = 'system_menu_type',
  SYSTEM_ROLE_TYPE = 'system_role_type',
  SYSTEM_DATA_SCOPE = 'system_data_scope',
  SYSTEM_NOTICE_TYPE = 'system_notice_type',
  SYSTEM_LOGIN_TYPE = 'system_login_type',
  SYSTEM_LOGIN_RESULT = 'system_login_result',
  SYSTEM_SMS_CHANNEL_CODE = 'system_sms_channel_code',
  SYSTEM_SMS_TEMPLATE_TYPE = 'system_sms_template_type',
  SYSTEM_SMS_SEND_STATUS = 'system_sms_send_status',
  SYSTEM_SMS_RECEIVE_STATUS = 'system_sms_receive_status',
  SYSTEM_ERROR_CODE_TYPE = 'system_error_code_type',
  SYSTEM_OAUTH2_GRANT_TYPE = 'system_oauth2_grant_type',
  SYSTEM_MAIL_SEND_STATUS = 'system_mail_send_status',
  SYSTEM_NOTIFY_TEMPLATE_TYPE = 'system_notify_template_type',
  SYSTEM_SOCIAL_TYPE = 'system_social_type',

  // ========== 账号与权限（本系统业务字典，待后端字典接口落地后切换）==========
  SYSTEM_ACCOUNT_STATUS = 'system_account_status', // 账号状态
  SYSTEM_ROLE_STATUS = 'system_role_status', // 角色状态
  SYSTEM_ENTRY_CLASS = 'system_entry_class', // 终端类型（角色/权限所属端，即入口类）
  SYSTEM_PERMISSION_STATUS = 'system_permission_status', // 权限点状态
  SYSTEM_PERM_LEVEL = 'system_perm_level', // 权限级别（L0~L4）
  SYSTEM_PERM_MODULE = 'system_perm_module', // 权限模块（模块类型）
  SYSTEM_GRANT_TYPE = 'system_grant_type', // 授权类型（永久/临时）
  SYSTEM_TEMP_GRANT_SCOPE = 'system_temp_grant_scope', // 临时授权范围（角色/权限点）
  SYSTEM_APPLY_TYPE = 'system_apply_type', // 申请类型
  SYSTEM_APPLY_STATUS = 'system_apply_status', // 申请状态（含审批阶段）
  SYSTEM_URGENCY = 'system_urgency', // 紧急程度
  SYSTEM_REJECT_REASON = 'system_reject_reason', // 审批驳回原因
  SYSTEM_OP_RESULT = 'system_op_result', // 操作结果（日志）

  // ========== 商学院模块 ==========
  BSCHOOL_COURSE_STATUS = 'bschool_course_status', // 课程状态
  BSCHOOL_MANDATORY = 'bschool_mandatory', // 是否强制学习
  BSCHOOL_EXAM_STATUS = 'bschool_exam_status', // 考试状态
  BSCHOOL_QUESTION_TYPE = 'bschool_question_type', // 题型
  BSCHOOL_QUESTION_DIFFICULTY = 'bschool_question_difficulty', // 题目难度
  BSCHOOL_ENABLE_STATUS = 'bschool_enable_status', // 启用状态（证书/题目共用）
  BSCHOOL_QUESTION_SOURCE = 'bschool_question_source', // 题目来源
  BSCHOOL_PASS_STATE = 'bschool_pass_state', // 合格判定
  BSCHOOL_USER_CERT_STATUS = 'bschool_user_cert_status', // 用户证书状态
  BSCHOOL_REVOKE_REASON = 'bschool_revoke_reason', // 证书撤销原因
  BSCHOOL_CERT_VALIDITY = 'bschool_cert_validity', // 证书有效期类型
  BSCHOOL_BEHAVIOR_TYPE = 'bschool_behavior_type', // 学习行为类型
  BSCHOOL_OBJECT_TYPE = 'bschool_object_type', // 行为对象类型
  BSCHOOL_TIME_RANGE = 'bschool_time_range', // 学习记录时间范围
  BSCHOOL_QUESTION_VIEW = 'bschool_question_view', // 题库视图

  // ========== 招商中心（channel 模块业务字典，basedata 里 category=BUSINESS/moduleCode=CHANNEL）==========
  CHANNEL_LEAD_SOURCE = 'channel_lead_source', // 线索来源
  CHANNEL_LEAD_STATUS = 'channel_lead_status', // 线索状态
  CHANNEL_FOLLOW_TYPE = 'channel_follow_type', // 跟进方式
  CHANNEL_LEAD_REASON = 'channel_lead_reason', // 线索关闭 / 退回原因
  CHANNEL_INTENT_ROLE = 'channel_intent_role', // 招商意向角色
  CHANNEL_SCRIPT_SCENE = 'channel_script_scene', // 话术场景
  CHANNEL_SCRIPT_ROLE = 'channel_script_role', // 话术适用角色
  CHANNEL_MATERIAL_AUDIENCE = 'channel_material_audience', // 招商资料适用角色
  CHANNEL_MATERIAL_USAGE = 'channel_material_usage', // 招商资料用途
  CHANNEL_MATERIAL_STATUS = 'channel_material_status', // 招商资料上下架状态
  CHANNEL_OPP_STAGE = 'channel_opp_stage', // 商机环节
  CHANNEL_STAGE_STATUS = 'channel_stage_status', // 商机环节内状态
  CHANNEL_STAGE_SUB_STATUS = 'channel_stage_sub_status', // 商机环节等待型子状态
  CHANNEL_AUDIT_LAYER = 'channel_audit_layer', // 商机审核链层级
  CHANNEL_PAY_METHOD = 'channel_pay_method', // 商机缴费方式
  CHANNEL_GRADUATED_TO = 'channel_graduated_to', // 商机毕业去向
  CHANNEL_INDUSTRY = 'channel_industry', // 招商行业
  CHANNEL_TERMINATE_REASON = 'channel_terminate_reason', // 商机终止原因

  // ========== INFRA 模块 ==========
  INFRA_BOOLEAN_STRING = 'infra_boolean_string',
  INFRA_JOB_STATUS = 'infra_job_status',
  INFRA_JOB_LOG_STATUS = 'infra_job_log_status',
  INFRA_API_ERROR_LOG_PROCESS_STATUS = 'infra_api_error_log_process_status',
  INFRA_CONFIG_TYPE = 'infra_config_type',
  INFRA_CODEGEN_TEMPLATE_TYPE = 'infra_codegen_template_type',
  INFRA_CODEGEN_FRONT_TYPE = 'infra_codegen_front_type',
  INFRA_CODEGEN_SCENE = 'infra_codegen_scene',
  INFRA_FILE_STORAGE = 'infra_file_storage',
  INFRA_OPERATE_TYPE = 'infra_operate_type',

  // ========== crm 模块 ==========
  CRM_PAYEE_TYPE = 'payee_type',
  CRM_FOLLOW_TYPE = 'follow_type',
  CRM_CUSTOMER_SOURCE = 'customer_source',
  CRM_CUSTOMER_INDUSTRY = 'customer_industry',
  CRM_CUSTOMER_LEVEL = 'customer_level',
  CRM_FOLLOW_STATUS = 'follow_status',
  CRM_BUSINESS_STATUS = 'business_status',
  CONTRACT_CHECK_STATUS = 'contract_check_status',
  CONTRACT_HANDLE_STATUS = 'contract_handle_status',

  // ========== MP 模块 ==========
  MP_AUTO_REPLY_REQUEST_MATCH = 'mp_auto_reply_request_match', // 自动回复请求匹配类型
  MP_MESSAGE_TYPE = 'mp_message_type' // 消息类型
}
